import { produce } from 'immer';
import { agentStreamError, AgentStreamErrorKind, Conversation } from '~api';
import { TApiClient } from '~portal/api';
import { logger } from '~portal/lib';
import { ConversationStore } from '~portal/stores';
import { SocketManager } from './SocketManager';

type SocketHandlers = Parameters<typeof SocketManager.configure>[1];

/**
 * @desc The socket → store event pipeline (H2-172), pure of React so it is directly testable:
 * every conversation event lands in the Zustand store keyed by its real conversation id,
 * Summarisation updates the sidebar caches, and a terminal signal (TurnComplete or a run
 * error) ends the "expecting updates" hold on the connection. `getTrpc` resolves the CURRENT
 * tRPC utils at call time — the SocketBridge keeps it fresh across re-renders.
 */
/**
 * @desc Whether this event ends the turn. A retryable ModelCallFailed does NOT: the agent is
 * expected to call the model again, and the turn is still running.
 */
const isTerminal = (event: Conversation.ClientEvent): boolean =>
  Conversation.isSystemEventByCode('TurnComplete')(event) ||
  (Conversation.isSystemEventByCode('ModelCallFailed')(event) && !event.payload.retryable);

export function createSocketHandlers(getTrpc: () => TApiClient): SocketHandlers {
  const finishTurn = (conversationId: string) => {
    ConversationStore.removeActiveAgent(conversationId);
    SocketManager.release(conversationId);
  };

  const handleSummary = (
    conversationId: string,
    event: Conversation.SystemEventByCode<'Summarisation'>,
  ) => {
    const { summary, comment } = event.payload;
    if (comment) logger.warn('[SocketBridge] summary generation note', { comment });
    if (!summary) return;
    ConversationStore.updateSummary(conversationId, summary);
    // Mutate the tRPC cache directly so the sidebar listing updates immediately.
    getTrpc().conversations.list.setData(undefined, (oldData) =>
      produce(oldData, (draft) => {
        const conv = draft?.find((c) => c._id === conversationId);
        if (conv) conv.summary = summary;
      }),
    );
  };

  return {
    onEvent: (conversationId, event) => {
      if (Conversation.isSystemEventByCode('Summarisation')(event)) {
        handleSummary(conversationId, event);
      }
      ConversationStore.addHistoryEvent(conversationId, event);
      // Two terminal signals, not one. TurnComplete is the ordinary end; a non-retryable
      // ModelCallFailed is the end of a turn that produced nothing, and the reducer does emit a
      // TurnComplete straight after it — but keying only off that would make the hold on the
      // connection depend on a second event surviving the trip, for the exact turns where things
      // are already going wrong. Both paths run `finishTurn`, which is idempotent.
      if (isTerminal(event)) {
        finishTurn(conversationId);
      }
    },

    onError: (conversationId, message) => {
      // Terminal run failure with no history event behind it — a write that never landed, an
      // abort, a watchdog timeout. The message is written by the run side FOR the user and is
      // rendered verbatim; `Internal` because a failure we could not persist is, by definition,
      // ours. A failure the conversation DID record arrives on `onEvent` instead and is reduced
      // from the event, so it survives a reload — this channel cannot.
      logger.error('[SocketBridge] turn failed', { conversationId, message });
      ConversationStore.setTurnError(
        conversationId,
        agentStreamError(AgentStreamErrorKind.Internal, message),
      );
      finishTurn(conversationId);
    },

    onReconnect: (activeConversationIds) => {
      // A gap may have dropped events: replay each still-active conversation from the
      // source of truth, then re-verify a run is actually still in flight.
      for (const conversationId of activeConversationIds) {
        void (async () => {
          try {
            const data = await getTrpc().client.conversations.get.query({ conversationId });
            ConversationStore.setConversation(data);
            const active = await SocketManager.watch(conversationId);
            if (!active) ConversationStore.removeActiveAgent(conversationId);
          } catch (err) {
            logger.error('[SocketBridge] replay after reconnect failed', {
              conversationId,
              error: err instanceof Error ? err.message : String(err),
            });
          }
        })();
      }
    },
  };
}
